import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Sparkles, Brush, Compass, Lightbulb, Headphones, ArrowUpRight, Palette } from "lucide-react";
import { Overline, WarliDivider } from "./Motifs";
import { MEERA_BOOKS, MAGIC_INK } from "../data/content";

const PILLARS = [
  { icon: Sparkles, label: "Storytelling", text: "A protagonist children fall in love with." },
  { icon: Brush, label: "Art activities", text: "Hands-on prompts after every chapter." },
  { icon: Compass, label: "Cultural exploration", text: "A different art form in every book." },
  { icon: Lightbulb, label: "Creative thinking", text: "Open-ended questions, no single answers." },
];

export default function MeeraSeries() {
  return (
    <section
      id="meera"
      data-testid="meera-section"
      className="relative py-24 sm:py-32 bg-[#F3EDE4]/60 border-y border-[#E5DFD3]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Intro */}
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Overline>The Flagship Series</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              Meera and her
              <br />
              <em className="italic font-light">Artistic Adventures</em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base sm:text-lg text-[#5C6A8F] leading-relaxed">
              A children&apos;s book series introducing young readers to India&apos;s rich artistic traditions
              through stories, activities, and creative exploration. Meera travels through kolams
              and Warli walls, through Madhubani villages and Pattachitra scrolls — each book a
              doorway into a living tradition.
            </p>
          </div>
        </div>

        <WarliDivider className="w-full h-12 mt-14 text-[#1E2A4F]/40" />

        {/* Books grid */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {MEERA_BOOKS.map((book, idx) => (
            <motion.article
              key={book.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`meera-book-${book.id}`}
              className="book-card group bg-white rounded-lg border border-[#E5DFD3] overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={book.cover}
                  alt={book.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {book.upcoming && (
                  <span className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase bg-[#1E2A4F] text-[#F9F6F0] px-3 py-1.5 rounded-full">
                    Coming Soon
                  </span>
                )}
                {book.comingSoon && !book.upcoming && (
                  <span
                    className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase text-[#F9F6F0] px-3 py-1.5 rounded-full"
                    style={{ background: book.accent }}
                  >
                    Coming Soon
                  </span>
                )}
              </div>
              <div className="p-6 sm:p-7 flex flex-col flex-1">
                <span className="text-[10px] tracking-[0.24em] uppercase font-medium" style={{ color: book.accent }}>
                  {book.artForm}
                </span>
                <h3 className="font-display text-2xl sm:text-3xl text-[#1E2A4F] mt-3 leading-tight">
                  {book.title}
                </h3>
                <p className="text-xs tracking-wide text-[#5C6A8F] mt-2 uppercase">{book.ageGroup}</p>
                <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-4 flex-1">{book.blurb}</p>
                {book.purchase && book.purchase !== "#" ? (
                  <a
                    href={book.purchase}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`meera-book-${book.id}-purchase`}
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#1E2A4F] ink-link self-start"
                  >
                    Purchase <ExternalLink size={14} />
                  </a>
                ) : (
                  <span
                    className="mt-6 text-xs uppercase tracking-[0.22em]"
                    style={{ color: book.accent }}
                  >
                    {book.upcoming
                      ? "Sign up below for news"
                      : book.comingSoon
                      ? "On the way — coming soon"
                      : "Link coming soon"}
                  </span>
                )}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Interactive learning pillars */}
        <div className="mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <Overline>Interactive Learning</Overline>
            <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-4 leading-tight">
              Each book is a doorway, not a destination.
            </h3>
            <p className="text-[#5C6A8F] mt-4 leading-relaxed">
              Meera doesn&apos;t just tell a story — she invites children to pick up a pencil, a chalk,
              a fistful of rice flour, and continue the adventure on their own kitchen floor.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {PILLARS.map(({ icon: Icon, label, text }) => (
              <div
                key={label}
                className="p-6 sm:p-7 rounded-lg border border-[#E5DFD3] bg-white hover:border-[#D95D39] transition-colors duration-500"
              >
                <Icon size={22} className="text-[#D95D39]" />
                <h4 className="font-display text-xl text-[#1E2A4F] mt-4">{label}</h4>
                <p className="text-sm text-[#5C6A8F] mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Art Studio CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="studio-cta"
          className="mt-16 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center rounded-lg overflow-hidden bg-[#F5B400] text-[#1E2A4F] border-2 border-[#1E2A4F]/10 relative"
        >
          <div className="absolute -right-12 -bottom-12 w-56 h-56 rounded-full opacity-20 bg-[#D95D39] pointer-events-none" />
          <div className="lg:col-span-8 p-8 sm:p-12 relative">
            <div className="inline-flex items-center gap-2 text-[#D95D39]">
              <Palette size={18} />
              <Overline className="!text-[#D95D39]">New · for young readers</Overline>
            </div>
            <h3 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              Make your own magic kolam in <em className="italic font-light">Meera&apos;s Art Studio</em>.
            </h3>
            <p className="mt-4 max-w-xl text-[#1E2A4F]/80 leading-relaxed">
              An interactive corner of the website where children can learn about the kolam,
              trace one with their finger, colour it in, and save the artwork. Free, no sign-up,
              works on phones and tablets.
            </p>
            <Link
              to="/studio"
              data-testid="meera-section-studio-link"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1E2A4F] text-[#F9F6F0] text-sm tracking-wide hover:bg-[#D95D39] transition-colors duration-500"
            >
              Open the Art Studio <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-4 p-6 sm:p-10 relative">
            <svg viewBox="0 0 200 200" className="w-full h-auto max-w-xs mx-auto" aria-hidden="true">
              {[40, 80, 120, 160].map((y) =>
                [40, 80, 120, 160].map((x) => (
                  <circle key={`${x}-${y}`} cx={x} cy={y} r="3" fill="#1E2A4F" />
                ))
              )}
              <path d="M 40 100 Q 100 30 160 100 Q 100 170 40 100 Z" fill="none" stroke="#D95D39" strokeWidth="3" />
              <path d="M 100 40 Q 170 100 100 160 Q 30 100 100 40 Z" fill="none" stroke="#1E2A4F" strokeWidth="3" />
              <circle cx="100" cy="100" r="10" fill="#D95D39" />
            </svg>
          </div>
        </motion.div>

        {/* Magic Ink audiobook callout */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="magic-ink-callout"
          className="mt-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center rounded-lg overflow-hidden bg-[#1E2A4F] text-[#F9F6F0]"
        >
          <div className="lg:col-span-7 p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 text-[#E76F51]">
              <Headphones size={18} />
              <Overline className="!text-[#E76F51]">{MAGIC_INK.tagline}</Overline>
            </div>
            <h3 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              {MAGIC_INK.title} — listen to the stories.
            </h3>
            <p className="mt-4 text-[#F9F6F0]/75 leading-relaxed max-w-md">
              {MAGIC_INK.blurb}
            </p>
            <a
              href={MAGIC_INK.spotify}
              target="_blank"
              rel="noreferrer"
              data-testid="magic-ink-spotify-link"
              className="mt-6 inline-flex items-center gap-2 text-sm ink-link"
            >
              Open Magic Ink on Spotify <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="lg:col-span-5 p-6 sm:p-8 lg:pr-10">
            <div className="rounded-md overflow-hidden border border-[#F9F6F0]/15">
              <iframe
                title="Spotify - Magic Ink audiobooks"
                src={MAGIC_INK.embed}
                width="100%"
                height="232"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                data-testid="magic-ink-embed"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
